// Miscellaneous.jsx
import React from "react";
import {
  Box,
  Container,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Input,
  NumberInput,
  NumberInputField,
  SimpleGrid,
  Textarea,
  useToast,
} from "@chakra-ui/react";
import axiosInstance from "../../Services/axiosInstance";
import PrimaryButton from "../../components/themeComponents/PrimaryButton";

const Miscellaneous = () => {
  const toast = useToast();

  const handleSubmit = (path, httpMethod = "POST") => async (e) => {
    e.preventDefault();

    const formData = new FormData(e.currentTarget);

    // For GET requests (like unsubscribe), we don't use FormData body
    if (httpMethod === "GET") {
      const phone = formData.get("phone")?.trim();
      const email = formData.get("email")?.trim();

      const params = new URLSearchParams();
      if (phone) params.append("phone", phone);
      if (email) params.append("email", email);

      const url = `/unsubscribe/?${params.toString()}`;

      try {
        await axiosInstance.get(url);

        toast({
          title: "Success",
          description: "Contact unsubscribed successfully",
          status: "success",
          isClosable: true,
        });
      } catch (err) {
        toast({
          title: "Error",
          description: err.response?.data?.message || "Failed to unsubscribe",
          status: "error",
          isClosable: true,
        });
      }
      return;
    }

    // For POST/PATCH requests – append method if backend expects it
    // Some backends use _method for method overriding (common in Laravel, etc.)
    // Adjust based on your backend convention
    // Option 1: Include as field (common with method spoofing)
    formData.append("_method", httpMethod === "PATCH" ? "PATCH" : "POST");

    // Option 2: Or just let axios use the correct method (preferred if no spoofing needed)
    const axiosMethod = httpMethod.toLowerCase();

    try {
      await axiosInstance[axiosMethod](path, formData, {
        headers: {
          // Let browser set Content-Type with boundary automatically
          // "Content-Type": "multipart/form-data", // ← Don't set manually
        },
      });

      toast({
        title: "Success",
        description: `Saved ${path.split("/").pop() || "changes"}`,
        status: "success",
        isClosable: true,
      });
    } catch (err) {
      toast({
        title: "Error",
        description: err.response?.data?.message || "Something went wrong",
        status: "error",
        isClosable: true,
      });
    }
  };

  return (
    <Container maxW="6xl" py={10}>
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
        {/* LEFT COLUMN */}
        <Flex direction="column" gap={6}>
          {/* Email cap */}
          <Box
            as="form"
            onSubmit={handleSubmit("/integration/cappingLimit?channel=email")}
            p={5}
            borderWidth="1px"
            borderRadius="lg"
          >
            <Heading size="sm" mb={4}>
              Email capping
            </Heading>
            <FormControl mb={4}>
              <FormLabel>Limit on sending Email (0 = infinite)</FormLabel>
              <NumberInput defaultValue={10} min={0}>
                <NumberInputField name="cappinglimit" />
              </NumberInput>
            </FormControl>
            <PrimaryButton type="submit" label="Save email cap" width="full" />
          </Box>

          {/* SMS cap */}
          <Box
            as="form"
            onSubmit={handleSubmit("/integration/cappingLimit?channel=sms")}
            p={5}
            borderWidth="1px"
            borderRadius="lg"
          >
            <Heading size="sm" mb={4}>
              SMS capping
            </Heading>
            <FormControl mb={4}>
              <FormLabel>Limit on sending SMS (0 = infinite)</FormLabel>
              <NumberInput defaultValue={10} min={0}>
                <NumberInputField name="smscappinglimit" />
              </NumberInput>
            </FormControl>
            <PrimaryButton type="submit" label="Save SMS cap" width="full" />
          </Box>

          {/* WhatsApp cap */}
          <Box
            as="form"
            onSubmit={handleSubmit("/integration/cappingLimit?channel=whatsapp")}
            p={5}
            borderWidth="1px"
            borderRadius="lg"
          >
            <Heading size="sm" mb={4}>
              WhatsApp capping
            </Heading>
            <FormControl mb={4}>
              <FormLabel>Limit on sending WhatsApp (0 = infinite)</FormLabel>
              <NumberInput defaultValue={10} min={0}>
                <NumberInputField name="whatsappcappinglimit" />
              </NumberInput>
            </FormControl>
            <PrimaryButton
              type="submit"
              label="Save WhatsApp cap"
              width="full"
            />
          </Box>

          {/* Unsubscribe contacts - Now uses GET with query params */}
          <Box
            as="form"
            onSubmit={handleSubmit("/unsubscribe/", "GET")}
            p={5}
            borderWidth="1px"
            borderRadius="lg"
          >
            <Heading size="sm" mb={4}>
              Unsubscribe contacts
            </Heading>
            <FormControl mb={3}>
              <FormLabel>Phone number (optional)</FormLabel>
              <Input name="phone" placeholder="Enter phone number" />
            </FormControl>
            <FormControl mb={4}>
              <FormLabel>Email address (optional)</FormLabel>
              <Input name="email" type="email" placeholder="Enter email address" />
            </FormControl>
            <PrimaryButton
              type="submit"
              label="Unsubscribe"
              colorScheme="red"
              width="full"
            />
          </Box>

          {/* Retry settings */}
          <Box
            as="form"
            onSubmit={handleSubmit("/broadcast/campaignFailConfig")}
            p={5}
            borderWidth="1px"
            borderRadius="lg"
          >
            <Heading size="sm" mb={4}>
              Failure retry settings
            </Heading>
            <FormControl mb={3}>
              <FormLabel>Delay (hours)</FormLabel>
              <NumberInput defaultValue={1} min={0}>
                <NumberInputField name="delay" />
              </NumberInput>
            </FormControl>
            <FormControl mb={4}>
              <FormLabel>Percentage</FormLabel>
              <NumberInput defaultValue={70} min={0} max={100}>
                <NumberInputField name="percentage" />
              </NumberInput>
            </FormControl>
            <PrimaryButton
              type="submit"
              label="Save retry settings"
              width="full"
            />
          </Box>
        </Flex>

        {/* RIGHT COLUMN */}
        <Flex direction="column" gap={6}>
          {/* User mapping schema */}
          <Box
            as="form"
            onSubmit={handleSubmit("/integration/usermappingschema", "POST")} // Often schemas are updated via PATCH
            p={5}
            borderWidth="1px"
            borderRadius="lg"
          >
            <Heading size="sm" mb={4}>
              User mapping schema (JSON)
            </Heading>
            <FormControl mb={4}>
              <FormLabel>Schema</FormLabel>
              <Textarea
                name="usermappingSchema"
                rows={6}
                placeholder='{"name": "user_name"}'
              />
            </FormControl>
            <Flex justify="space-between" gap={3}>
              <PrimaryButton
                variant="outline"
                size="sm"
                type="button"
                label="Download sample"
              />
              <PrimaryButton
                type="submit"
                label="Save user schema"
                flex={1}
              />
            </Flex>
          </Box>

          {/* Item mapping schema */}
          <Box
            as="form"
            onSubmit={handleSubmit("/integration/itemmappingschema", "POST")}
            p={5}
            borderWidth="1px"
            borderRadius="lg"
          >
            <Heading size="sm" mb={4}>
              Item mapping schema (JSON)
            </Heading>
            <FormControl mb={4}>
              <FormLabel>Schema</FormLabel>
              <Textarea
                name="itemmappingSchema"
                rows={6}
                placeholder='{"item": "product_name"}'
              />
            </FormControl>
            <Flex justify="space-between" gap={3}>
              <PrimaryButton
                variant="outline"
                size="sm"
                type="button"
                label="Download sample"
              />
              <PrimaryButton
                type="submit"
                label="Save item schema"
                flex={1}
              />
            </Flex>
          </Box>

          {/* Coupon code */}
          <Box
            as="form"
            onSubmit={handleSubmit("/smsreport/save/coupon")}
            p={5}
            borderWidth="1px"
            borderRadius="lg"
          >
            <Heading size="sm" mb={4}>
              Coupon code
            </Heading>
            <FormControl mb={4}>
              <FormLabel>Enter coupon code</FormLabel>
              <Input name="coupon" placeholder="COUPON2025" />
            </FormControl>
            <PrimaryButton
              type="submit"
              label="Save coupon"
              width="full"
            />
          </Box>

          {/* Coupon upload */}
          <Box
            as="form"
            encType="multipart/form-data"
            onSubmit={handleSubmit("/coupon/upload")}
            p={5}
            borderWidth="1px"
            borderRadius="lg"
          >
            <Heading size="sm" mb={2}>
              Upload your coupons
            </Heading>
            <Box mb={4} fontSize="sm" color="gray.500">
              Only .jpg and .png files. 500kb max file size.
            </Box>
            <FormControl mb={4}>
              <Input
                name="couponFiles"
                type="file"
                accept=".jpg,.jpeg,.png"
                multiple
                p={1}
              />
            </FormControl>
            <PrimaryButton
              type="submit"
              label="Upload files"
              width="full"
            />
          </Box>
        </Flex>
      </SimpleGrid>
    </Container>
  );
};

export default Miscellaneous;